import React, { useRef, useState } from "react";

import { toHistoryMonthInput } from "../../shared/helpers";
import { HistoryItem } from "../../shared/siteContent";
import { Pager, SectionCard } from "../ui";

type HistorySectionProps = {
  rows: HistoryItem[];
  page: number;
  totalPages: number;
  start: number;
  onPageChange: (page: number) => void;
  onAddItem: () => void;
  onRemoveItem: (absoluteIndex: number) => void;
  onUpdateMonth: (absoluteIndex: number, monthInput: string) => void;
  onUpdateLine: (absoluteIndex: number, lineIndex: number, value: string) => void;
  onAddLine: (absoluteIndex: number) => void;
  onRemoveLine: (absoluteIndex: number, lineIndex: number) => void;
  onReorderCards: (fromIndex: number, toIndex: number) => void;
  onReorderLines: (cardIndex: number, fromIndex: number, toIndex: number) => void;
  onOpenPreview: () => void;
  onSave: () => void;
  saving: boolean;
};

/**
 * History 편집 섹션.
 * 카드(연혁 항목)와 라인(항목 내부 텍스트) 각각의 드래그 정렬을 지원한다.
 */
export function HistorySection({
  rows,
  page,
  totalPages,
  start,
  onPageChange,
  onAddItem,
  onRemoveItem,
  onUpdateMonth,
  onUpdateLine,
  onAddLine,
  onRemoveLine,
  onReorderCards,
  onReorderLines,
  onOpenPreview,
  onSave,
  saving,
}: HistorySectionProps) {
  // 카드(연혁 항목)와 라인(항목 내부 문장)을 분리해 드래그 상태를 관리한다.
  const listRef = useRef<HTMLDivElement | null>(null);
  const [draggingCard, setDraggingCard] = useState<number | null>(null);
  const [dragOverCard, setDragOverCard] = useState<number | null>(null);
  const [draggingLine, setDraggingLine] = useState<{ cardIndex: number; lineIndex: number } | null>(null);
  const [dragOverLine, setDragOverLine] = useState<{ cardIndex: number; lineIndex: number } | null>(null);

  // 드래그 위치에 따라 컨테이너 스크롤을 자동 보정한다.
  function autoScroll(event: React.DragEvent<HTMLDivElement>) {
    const container = listRef.current;
    if (!container) return;
    const bounds = container.getBoundingClientRect();
    // 드래그 포인터가 경계 근처에 오면 자동 스크롤해 긴 목록에서도 재정렬이 가능하게 한다.
    const threshold = 44;
    const delta = event.clientY < bounds.top + threshold ? -12 : event.clientY > bounds.bottom - threshold ? 12 : 0;
    if (!delta) return;
    container.scrollTop += delta;
  }

  return (
    <div className="cm-panel-grid">
      <div className="cm-actions">
        <button className="cm-btn cm-btn-preview" onClick={onOpenPreview} type="button">
          미리보기
        </button>
        <button className="cm-btn" disabled={saving} onClick={onSave} type="button">
          {saving ? "저장 중..." : "History 저장"}
        </button>
        <button className="cm-btn cm-btn-secondary" onClick={onAddItem} style={{ marginLeft: "auto" }} type="button">
          History 항목 추가
        </button>
      </div>
      <div
        className="cm-panel-grid"
        ref={listRef}
        style={{ maxHeight: 620, overflow: "auto" }}
        onDragOver={(event) => {
          // 컨테이너 레벨에서 auto-scroll을 유지해 카드/라인 드래그를 끊김 없이 연결한다.
          if (draggingCard == null && draggingLine == null) return;
          event.preventDefault();
          autoScroll(event);
        }}
        onDrop={() => {
          setDraggingCard(null);
          setDragOverCard(null);
          setDraggingLine(null);
          setDragOverLine(null);
        }}
      >
        {rows.map((item, localIndex) => {
          const absoluteIndex = start + localIndex;
          return (
            <div
              className={`cm-history-card-wrap ${dragOverCard === absoluteIndex ? "cm-drop-indicator-card" : ""}`.trim()}
              key={item.id}
              onDragOver={(event) => {
                if (draggingCard == null) return;
                event.preventDefault();
                autoScroll(event);
                setDragOverCard(absoluteIndex);
              }}
                onDrop={(event) => {
                  event.preventDefault();
                  if (draggingCard == null) return;
                  // drop 위치를 절대 인덱스로 계산해 페이지네이션 상태에서도 순서를 정확히 유지한다.
                  onReorderCards(draggingCard, absoluteIndex);
                  setDraggingCard(null);
                  setDragOverCard(null);
                }}
            >
              <SectionCard
                className="cm-history-card"
                title={item.year || `History #${absoluteIndex + 1}`}
                actions={
                  <>
                    <button
                      className="cm-drag-handle"
                      draggable
                      onDragEnd={() => {
                        setDraggingCard(null);
                        setDragOverCard(null);
                      }}
                      onDragStart={() => setDraggingCard(absoluteIndex)}
                      title="항목 드래그 정렬"
                      type="button"
                    >
                      ⋮⋮
                    </button>
                    <span className={`cm-pill ${/^\d{4}\.\d{2}$/.test(item.year) ? "ok" : "warn"}`.trim()}>{item.year || "형식 확인 필요"}</span>
                    <button className="cm-btn cm-btn-secondary" onClick={() => onAddLine(absoluteIndex)} type="button">
                      라인추가
                    </button>
                    <button
                      className="cm-btn cm-btn-danger"
                      onClick={() => {
                        if (!window.confirm("이 History 항목을 삭제하시겠습니까?")) return;
                        onRemoveItem(absoluteIndex);
                      }}
                      type="button"
                    >
                      삭제
                    </button>
                  </>
                }
              >
                <div className={`cm-row-editor ${draggingCard === absoluteIndex ? "is-dragging" : ""}`.trim()} style={{ gridTemplateColumns: "1fr auto" }}>
                  <input
                    className={`cm-input ${item.year && !/^\d{4}\.\d{2}$/.test(item.year) ? "error" : ""}`.trim()}
                    onChange={(event) => onUpdateMonth(absoluteIndex, event.target.value)}
                    type="month"
                    value={toHistoryMonthInput(item.year || "")}
                  />
                  <span className="cm-pill">{(item.contents || []).length} lines</span>
                  </div>
                <div className="cm-panel-grid">
                  {(item.contents || []).map((line, lineIndex) => (
                    <div
                      className={`cm-row-editor ${
                        draggingLine?.cardIndex === absoluteIndex && draggingLine?.lineIndex === lineIndex ? "is-dragging" : ""
                      } ${dragOverLine?.cardIndex === absoluteIndex && dragOverLine?.lineIndex === lineIndex ? "cm-drop-indicator-line" : ""}`.trim()}
                      style={{ gridTemplateColumns: "auto 1fr auto" }}
                      key={`${item.id}-${lineIndex}`}
                      onDragOver={(event) => {
                        if (!draggingLine || draggingLine.cardIndex !== absoluteIndex) return;
                        event.preventDefault();
                        autoScroll(event);
                        setDragOverLine({ cardIndex: absoluteIndex, lineIndex });
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        if (!draggingLine || draggingLine.cardIndex !== absoluteIndex) return;
                        onReorderLines(absoluteIndex, draggingLine.lineIndex, lineIndex);
                        setDraggingLine(null);
                        setDragOverLine(null);
                      }}
                    >
                      <button
                        className="cm-drag-handle"
                        draggable
                        onDragEnd={() => {
                          setDraggingLine(null);
                          setDragOverLine(null);
                        }}
                        onDragStart={() => setDraggingLine({ cardIndex: absoluteIndex, lineIndex })}
                        title="라인 드래그 정렬"
                        type="button"
                      >
                        ⋮⋮
                      </button>
                      <input className="cm-input" onChange={(event) => onUpdateLine(absoluteIndex, lineIndex, event.target.value)} value={line || ""} />
                      <button
                        className="cm-btn cm-btn-danger cm-btn-icon"
                        onClick={() => {
                          if (!window.confirm("이 라인을 삭제하시겠습니까?")) return;
                          onRemoveLine(absoluteIndex, lineIndex);
                        }}
                        title="라인 삭제"
                        type="button"
                      >
                        -
                      </button>
                    </div>
                  ))}
                </div>
              </SectionCard>
            </div>
          );
        })}
      </div>
      <Pager page={page} totalPages={totalPages} onPageChange={onPageChange} />
    </div>
  );
}
